import { WizardStep } from './types';

/**
 * Who holds the single-flight AI generation lock, and what to tell everyone else.
 *
 * `isGenerating` is global — one generation at a time across the whole editor —
 * so on its own it cannot tell a button "you are working" apart from "someone
 * else is working". Reading it as the former is what used to put a spinner on
 * every AI button in the report. `generatingSectionId` is the signal that
 * separates the two.
 */

export interface AiGenerationLockInput {
    isGenerating: boolean;
    generatingSectionId: string | null;
    /** Resolved sidebar steps — user renames and custom sections included. */
    sidebarSteps: WizardStep[];
    /** The section whose button is asking. */
    ownerId?: string;
}

export interface AiGenerationLock {
    /** This owner's own generation is in flight; show the spinner. */
    isGeneratingThis: boolean;
    /** Someone else holds the lock, and this sentence says who. */
    blockedByOtherReason: string | null;
}

export const getGeneratingSectionTitle = (
    generatingSectionId: string | null,
    sidebarSteps: WizardStep[],
): string | null => {
    if (!generatingSectionId) return null;
    return sidebarSteps.find((step) => step.id === generatingSectionId)?.title ?? null;
};

/**
 * Why this button cannot run, and what to do about it.
 *
 * It stopped saying "Aguarde" when the generation became interruptible: waiting
 * is no longer the only option, and a message that names a route the product
 * now has is worth more than one that asks for patience it no longer needs.
 *
 * The unnamed variant is not dead code: a section can be deleted mid-flight,
 * and any future generation entry point that forgets to set a section id lands
 * here rather than on an empty pair of quotes.
 */
export const buildAiBusyReason = (title: string | null): string =>
    title
        ? `A IA está gerando "${title}". Interrompa para gerar outra seção.`
        : 'Outra geração com IA está em andamento. Interrompa para gerar esta seção.';

export function resolveAiGenerationLock({
    isGenerating,
    generatingSectionId,
    sidebarSteps,
    ownerId,
}: AiGenerationLockInput): AiGenerationLock {
    if (!isGenerating) {
        return { isGeneratingThis: false, blockedByOtherReason: null };
    }

    // An owner-less caller is never the holder, so it reads as blocked. That is
    // the safe way round: it disables a button that cannot run anyway, where the
    // opposite would claim someone else's work as its own.
    if (ownerId && generatingSectionId === ownerId) {
        return { isGeneratingThis: true, blockedByOtherReason: null };
    }

    return {
        isGeneratingThis: false,
        blockedByOtherReason: buildAiBusyReason(
            getGeneratingSectionTitle(generatingSectionId, sidebarSteps),
        ),
    };
}
